import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf";
import { FileText, Presentation, Download, Loader2, Ban } from "lucide-react";
import { API_BASE } from "@/lib/api";

pdfjsLib.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.js";

/* PUBLIC pitch viewer — /p/:token
   The client opens the recruiter's share link: shows the one-pager PDF inline
   plus download buttons, styled with the client's own branding colors.
   Every page load is tracked server-side (open counter + notification). */

const LBL = {
    nl: { kicker: "Kandidaat-presentatie", prepared: "Opgesteld voor", dl_pdf: "Download one-pager (PDF)",
          dl_ppt: "Download presentatie (PPT)", gone: "Deze link is niet (meer) beschikbaar.",
          loading: "Presentatie laden…" },
    en: { kicker: "Candidate presentation", prepared: "Prepared for", dl_pdf: "Download one-pager (PDF)",
          dl_ppt: "Download presentation (PPT)", gone: "This link is no longer available.",
          loading: "Loading presentation…" },
    fr: { kicker: "Présentation du candidat", prepared: "Préparé pour", dl_pdf: "Télécharger le one-pager (PDF)",
          dl_ppt: "Télécharger la présentation (PPT)", gone: "Ce lien n'est plus disponible.",
          loading: "Chargement de la présentation…" },
};

export default function PitchViewer() {
    const { token } = useParams();
    const [meta, setMeta] = useState(null);
    const [gone, setGone] = useState(false);
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfData, setPdfData] = useState(null);
    const [rendering, setRendering] = useState(true);
    const canvasHostRef = useRef(null);

    useEffect(() => {
        let objectUrl = null;
        (async () => {
            try {
                const r = await axios.get(`${API_BASE}/public/pitch-share/${token}`);
                setMeta(r.data);
                if (r.data.has_pdf) {
                    const f = await axios.get(`${API_BASE}/public/pitch-share/${token}/file/pdf`,
                        { responseType: "arraybuffer" });
                    objectUrl = URL.createObjectURL(new Blob([f.data], { type: "application/pdf" }));
                    setPdfUrl(objectUrl);
                    setPdfData(f.data);
                } else {
                    setRendering(false);
                }
            } catch {
                setGone(true);
                setRendering(false);
            }
        })();
        return () => { if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [token]);

    // Render once both the PDF bytes and the canvas host (post-meta render) exist.
    useEffect(() => {
        if (!pdfData || !canvasHostRef.current) return;
        (async () => {
            try { await renderPdf(pdfData); }
            catch { /* download buttons still work */ }
            finally { setRendering(false); }
        })();
    }, [pdfData, meta]);

    /* Render every PDF page to a canvas — works on all browsers incl. mobile,
       unlike <iframe src=blob> which many engines refuse to display inline. */
    const renderPdf = async (arrayBuffer) => {
        const host = canvasHostRef.current;
        if (!host) return;
        const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
        host.innerHTML = "";
        const targetWidth = Math.min(host.clientWidth || 860, 980);
        const dpr = window.devicePixelRatio || 1;
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const base = page.getViewport({ scale: 1 });
            const scale = targetWidth / base.width;
            const viewport = page.getViewport({ scale: scale * dpr });
            const canvas = document.createElement("canvas");
            canvas.width = viewport.width;
            canvas.height = viewport.height;
            canvas.style.width = `${targetWidth}px`;
            canvas.style.height = `${(viewport.height / dpr)}px`;
            canvas.style.borderRadius = "12px";
            canvas.style.display = "block";
            canvas.style.margin = "0 auto 16px";
            canvas.style.boxShadow = "0 8px 40px rgba(0,0,0,0.5)";
            canvas.setAttribute("data-testid", `pitch-viewer-page-${i}`);
            host.appendChild(canvas);
            await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        }
    };

    const L = LBL[meta?.language] || LBL.nl;
    const accent = meta?.branding?.accent || "#FF5A00";
    const dark = meta?.branding?.accent_dark || "#1a1a2e";

    const downloadPptx = () => {
        const a = document.createElement("a");
        a.href = `${API_BASE}/public/pitch-share/${token}/file/pptx`;
        a.click();
    };

    if (gone) {
        return (
            <div className="min-h-screen bg-[#101018] flex items-center justify-center p-6" data-testid="pitch-viewer-gone">
                <div className="text-center">
                    <Ban size={32} className="mx-auto text-white/30"/>
                    <p className="mt-4 text-white/70 text-sm">{LBL.nl.gone}</p>
                </div>
            </div>
        );
    }
    if (!meta) {
        return (
            <div className="min-h-screen bg-[#101018] flex items-center justify-center" data-testid="pitch-viewer-loading">
                <Loader2 size={24} className="animate-spin text-white/50"/>
            </div>
        );
    }

    return (
        <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#101018" }} data-testid="pitch-viewer">
            {/* Branded header */}
            <div style={{ backgroundColor: dark }} className="px-6 py-5 border-b-4" >
                <div className="max-w-4xl mx-auto flex items-center justify-between gap-4">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.24em] font-bold" style={{ color: accent }}>
                            {L.kicker}
                        </div>
                        <h1 className="text-white text-xl font-semibold mt-1" data-testid="pitch-viewer-title">
                            {meta.role_title || meta.candidate_display_name}
                        </h1>
                        <p className="text-white/60 text-xs mt-0.5">
                            {meta.candidate_display_name} · {L.prepared}: <b className="text-white/85">{meta.company_name}</b>
                        </p>
                    </div>
                    {meta.branding?.logo_url && (
                        <img src={meta.branding.logo_url} alt="" className="h-10 max-w-[120px] object-contain bg-white/95 rounded-lg p-1.5"/>
                    )}
                </div>
            </div>
            <div style={{ backgroundColor: accent, height: 4 }}/>

            {/* Actions */}
            <div className="max-w-4xl mx-auto w-full px-6 py-4 flex items-center gap-2 flex-wrap">
                {meta.has_pdf && pdfUrl && (
                    <a href={pdfUrl} download={`presentatie.pdf`} data-testid="pitch-viewer-dl-pdf"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium text-white transition-opacity hover:opacity-85"
                        style={{ backgroundColor: accent }}>
                        <FileText size={13}/> {L.dl_pdf}
                    </a>
                )}
                {meta.has_pptx && (
                    <button onClick={downloadPptx} data-testid="pitch-viewer-dl-pptx"
                        className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium border text-white/85 hover:bg-white/5"
                        style={{ borderColor: `${accent}80` }}>
                        <Presentation size={13}/> {L.dl_ppt}
                    </button>
                )}
            </div>

            {/* Inline PDF (rendered to canvas via pdf.js) */}
            <div className="flex-1 max-w-4xl mx-auto w-full px-6 pb-8">
                {meta.has_pdf ? (
                    <>
                        {rendering && (
                            <div className="flex items-center justify-center py-20 text-white/50 text-sm">
                                <Loader2 size={16} className="animate-spin mr-2"/> {L.loading}
                            </div>
                        )}
                        <div ref={canvasHostRef} data-testid="pitch-viewer-pdf"/>
                    </>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-white/50 text-sm gap-3">
                        <Presentation size={28} style={{ color: accent }}/>
                        <button onClick={downloadPptx} className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium text-white"
                            style={{ backgroundColor: accent }}>
                            <Download size={13}/> {L.dl_ppt}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}
